<template>
  <!-- 消息入口（显示未读消息+进度任务数量） -->
  <el-badge :value="totalBadge" :hidden="totalBadge === 0" :max="99" class="message-badge">
    <el-icon class="bell-icon" :class="{ shake: hasNewMessage }" @click="openDrawer" :size="20">
      <Bell />
    </el-icon>
  </el-badge>

  <!-- 消息抽屉 -->
  <el-drawer v-model="drawerVisible" title="消息中心" size="400px" direction="rtl" :append-to-body="true">
    <MessageCenter ref="messageCenterRef" @update-count="updateCount" @close="handleClose" @progress-update="updateProgressCount" />
  </el-drawer>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ElNotification } from 'element-plus'
import { Bell } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import MessageCenter from '@/components/MessageCenter.vue'
import { subscribeSse, getUnreadCount, getProcessingTasks, getMessages } from '@/api/message'

const router = useRouter()
const emit = defineEmits(['refresh-requested'])
const unreadCount = ref(0)
const progressCount = ref(0)
const hasNewMessage = ref(false)
const drawerVisible = ref(false)
const messageCenterRef = ref(null)

// 角标总数
const totalBadge = computed(() => unreadCount.value + progressCount.value)

// SSE 消息去重：记录已处理的 msgId
const processedMsgIds = new Set()
const MAX_DEDUP_SIZE = 500

// SSE 连接实例
let sseConnection = null
let pollTimer = null
let visibilityHandler = null

// 记录上一次轮询时"进行中"的任务ID集合，用于检测任务完成
let prevProcessingTaskIds = new Set()

// 轮询通知去重：避免轮询和SSE重复弹同一条消息的通知
// 使用 Map 记录 id → timestamp，定期清理超过5分钟的条目，防止内存泄漏
const notifiedMessageIds = new Map()
const NOTIFIED_TTL_MS = 5 * 60 * 1000

function cleanNotifiedIds() {
  const now = Date.now()
  for (const [id, ts] of notifiedMessageIds) {
    if (now - ts > NOTIFIED_TTL_MS) {
      notifiedMessageIds.delete(id)
    }
  }
}

/**
 * 初始化
 */
onMounted(() => {
  loadInitialData()
  startSse()

  // 兜底轮询（每10秒检测任务状态变化，SSE断连时补发通知）
  pollTimer = setInterval(pollFallback, 10000)

  // 页面可见性监听
  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      console.log('[SSE] 页面切回前台，刷新数据')
      loadInitialData()
      pollFallback()
      // 检查 SSE 状态，断开则立即重连
      if (sseConnection) {
        sseConnection.ensureConnected()
      }
      // 刷新进度条数据（后台期间 SSE 消息可能丢失，用 API 补齐）
      if (messageCenterRef.value) {
        messageCenterRef.value.loadProcessingTasks()
      }
    }
  }
  document.addEventListener('visibilitychange', visibilityHandler)
})

/**
 * 清理
 */
onUnmounted(() => {
  if (sseConnection) {
    sseConnection.close()
  }
  if (pollTimer) {
    clearInterval(pollTimer)
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
  }
})

/**
 * 加载初始数据
 */
async function loadInitialData() {
  try {
    unreadCount.value = await getUnreadCount()
    const tasks = await getProcessingTasks()
    progressCount.value = tasks.length || 0
    // 初始化进行中任务ID集合
    prevProcessingTaskIds = new Set(tasks.map(t => t.id))
  } catch (e) {
    console.error('[SSE] 加载数据失败:', e)
  }
}

/**
 * 兜底轮询：检测任务状态变化，SSE断连时补发通知
 * 
 * 核心逻辑：
 * 1. 获取当前进行中的任务列表
 * 2. 对比上次轮询的结果，找出"刚完成"的任务（之前在进行中，现在不在了）
 * 3. 如果有任务刚完成，拉取最新未读消息并弹出通知
 */
async function pollFallback() {
  try {
    // 定期清理过期的通知去重记录
    cleanNotifiedIds()

    const tasks = await getProcessingTasks()
    const currentTaskIds = new Set(tasks.map(t => t.id))
    progressCount.value = tasks.length || 0

    // 检测刚完成的任务：在上次集合中但不在当前集合中
    const completedTaskIds = [...prevProcessingTaskIds].filter(id => !currentTaskIds.has(id))
    
    // 更新未读数（任务完成会创建消息，未读数会变化）
    unreadCount.value = await getUnreadCount()

    // 有任务刚完成，且这些任务不在SSE已处理的范围内 → 补发通知
    if (completedTaskIds.length > 0) {
      console.log('[POLL] 检测到任务完成，补发通知:', completedTaskIds)
      // 更新进行中任务列表
      if (messageCenterRef.value) {
        completedTaskIds.forEach(taskId => {
          messageCenterRef.value.removeProgressTask(taskId)
        })
        messageCenterRef.value.loadProcessingTasks()
      }
      // 拉取最新消息列表，弹出通知
      await fetchAndNotifyLatestMessages(completedTaskIds.length)
    }

    // 更新任务ID集合
    prevProcessingTaskIds = currentTaskIds
  } catch (e) {
    console.error('[POLL] 轮询失败:', e)
  }
}

/**
 * 拉取最新未读消息并弹出通知（轮询兜底专用）
 * @param {number} expectedCount 预期新增的消息数量
 */
async function fetchAndNotifyLatestMessages(expectedCount) {
  try {
    const res = await getMessages({ pageNum: 1, pageSize: expectedCount + 5 })
    const messages = res.records || res.list || []
    
    // 收集所有未通知的未读消息
    const newMsgs = []
    for (const msg of messages) {
      if (msg.isRead === 0 && !notifiedMessageIds.has(msg.id)) {
        notifiedMessageIds.set(msg.id, Date.now())
        newMsgs.push(msg)
      }
    }

    if (newMsgs.length === 0) {
      emit('refresh-requested')
      return
    }

    // 铃铛动画
    hasNewMessage.value = true
    setTimeout(() => hasNewMessage.value = false, 1000)

    if (newMsgs.length === 1) {
      // 单条：弹详细通知
      const msg = newMsgs[0]
      ElNotification({
        title: msg.title,
        message: msg.content,
        type: msg.msgType === 'TASK_FAIL' ? 'error' : 'success',
        duration: 5000,
        position: 'top-right',
        onClick: () => {
          if (msg.routeName) {
            try {
              const params = typeof msg.routeParams === 'string'
                ? JSON.parse(msg.routeParams)
                : (msg.routeParams || {})
              router.push({ name: msg.routeName, params })
              drawerVisible.value = false
            } catch (e) {
              console.error('[POLL] 路由跳转失败:', e)
            }
          }
        }
      })
    } else {
      // 多条：弹汇总通知
      ElNotification({
        title: `${newMsgs.length}个任务已完成`,
        message: newMsgs.map(m => m.title).join('、'),
        type: 'info',
        duration: 5000,
        position: 'top-right',
        onClick: () => {
          drawerVisible.value = true
          setTimeout(() => {
            messageCenterRef.value?.loadMessages()
          }, 100)
        }
      })
    }

    // 通知父组件刷新
    emit('refresh-requested')
  } catch (e) {
    console.error('[POLL] 拉取消息失败:', e)
  }
}

/**
 * 打开抽屉
 */
function openDrawer() {
  drawerVisible.value = true
  setTimeout(() => {
    messageCenterRef.value?.loadMessages()
    messageCenterRef.value?.loadProcessingTasks()
  }, 100)
}

/**
 * 更新未读数
 */
function updateCount(count) {
  unreadCount.value = count
}

/**
 * 更新进度任务数
 */
function updateProgressCount(count) {
  progressCount.value = count
}

/**
 * 关闭抽屉
 */
function handleClose() {
  drawerVisible.value = false
}

/**
 * 建立 SSE 连接
 */
function startSse() {
  sseConnection = subscribeSse({
    onopen: () => {
      console.log('[SSE] 已连接')
    },
    onmessage: (data) => {
      handleSseMessage(data)
    }
  })
}

/**
 * 处理 SSE 消息
 */
function handleSseMessage(data) {
  // 忽略连接确认
  if (data.status === 'connected') return

  // 去重：补发重连时可能收到已处理过的消息
  if (data.msgId !== undefined) {
    if (processedMsgIds.has(data.msgId)) {
      console.log('[SSE] 跳过重复消息 msgId=' + data.msgId)
      return
    }
    processedMsgIds.add(data.msgId)
    // 防止 Set 无限增长
    if (processedMsgIds.size > MAX_DEDUP_SIZE) {
      const arr = [...processedMsgIds].sort((a, b) => a - b)
      const toRemove = arr.slice(0, arr.length - MAX_DEDUP_SIZE + 100)
      toRemove.forEach(id => processedMsgIds.delete(id))
    }
  }

  // 进度更新
  if (data.type === 'PROGRESS_UPDATE') {
    messageCenterRef.value?.updateProgress(data)
    return
  }

  // 任务完成/失败（来自completeTask/failTask的SSE推送）
  // 只做 UI 更新，不弹通知 —— 通知由后续 NEW_MESSAGE 统一弹出（信息更完整）
  // SSE 断连时由 10 秒轮询兜底补发通知
  if (data.type === 'TASK_COMPLETE' || data.type === 'TASK_FAIL') {
    messageCenterRef.value?.removeProgressTask(data.taskId)
    if (messageCenterRef.value) {
      messageCenterRef.value.loadProcessingTasks()
    }
    getUnreadCount().then(count => { unreadCount.value = count })
    emit('refresh-requested', data.routeName)
    return
  }

  // 任务完成消息
  if (data.type === 'NEW_MESSAGE') {
    // 去重：避免SSE和轮询重复通知
    if (data.messageId !== undefined) {
      notifiedMessageIds.set(data.messageId, Date.now())
    }
    // 移除进行中的任务
    if (data.taskId) {
      messageCenterRef.value?.removeProgressTask(data.taskId)
    }

    // 增加未读数
    unreadCount.value++

    // 刷新消息列表
    if (drawerVisible.value) {
      messageCenterRef.value?.loadMessages()
    }

    // 铃铛动画
    hasNewMessage.value = true
    setTimeout(() => hasNewMessage.value = false, 1000)

    // 弹出通知（NEW_MESSAGE 是任务完成通知的唯一来源，信息最完整）
    ElNotification({
      title: data.title,
      message: data.content,
      type: data.success ? 'success' : 'error',
      duration: 5000,
      position: 'top-right',
      onClick: () => {
        if (data.routeName) {
          try {
            const params = typeof data.routeParams === 'string'
              ? JSON.parse(data.routeParams)
              : data.routeParams
            router.push({ name: data.routeName, params })
            drawerVisible.value = false
          } catch (e) {
            console.error('[SSE] 路由跳转失败:', e)
          }
        }
      }
    })

    // 通知父组件刷新当前页
    emit('refresh-requested', data.routeName)
  }
}
</script>

<style scoped>
.message-badge {
  margin-right: 16px;
}

.bell-icon {
  cursor: pointer;
  color: #606266;
  transition: transform 0.3s;
}

.bell-icon:hover {
  color: #409eff;
}

.bell-icon.shake {
  animation: shake 0.5s ease-in-out;
  color: #e6a23c;
}

@keyframes shake {
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(15deg); }
  75% { transform: rotate(-15deg); }
}
</style>